import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { db, collection, addDoc } from '../firebase';
import { getCountFromServer } from 'firebase/firestore';
import Typewriter from 'typewriter-effect';
import { Link } from 'react-router-dom';

export default function HeradexiaWaitlist() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(null);

  useEffect(() => {
    const fetchWaitlistCount = async () => {
      try {
        const snapshot = await getCountFromServer(collection(db, 'waitlist'));
        setWaitlistCount(snapshot.data().count);
      } catch (err) {
        console.error("Error fetching count:", err);
      }
    };
    fetchWaitlistCount();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'waitlist'), {
        email,
        timestamp: new Date(),
      });
      setSubmitted(true);
      setEmail('');
    } catch (error) {
      console.error('Error saving email:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#0B1E3C] to-[#11263D] text-white font-sans px-4 relative overflow-hidden">
      {/* Dynamic Background Waves */}
      <div className="absolute top-0 left-0 w-full h-full z-0">
        <div className="absolute w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-purple-800/20 via-transparent to-transparent animate-pulse" />
        <div className="absolute w-full h-full bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-blue-800/20 via-transparent to-transparent animate-pulse" />
      </div>

      <header className="sticky top-0 z-50 flex flex-wrap justify-between items-center py-6 px-4 w-full bg-transparent">
        <div className="flex items-center space-x-3">
          <h1 className="text-2xl font-extrabold text-white tracking-wide">Heradexia</h1>
        </div>
        <nav className="space-x-6 text-sm font-medium mt-2 sm:mt-0">
          <Link to="/" className="hover:text-[#8BC53F]">Join Waitlist</Link>
          <Link to="/how-it-works" className="hover:text-[#8BC53F]">How It Works</Link>
          <Link to="/use-demo" className="hover:text-[#8BC53F]">Use Demo</Link>
        </nav>
      </header>

      <main className="max-w-5xl mx-auto text-center py-20 relative z-10">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-5xl md:text-6xl font-black leading-tight mb-6 tracking-tight text-balance"
        >
          <span className="text-white font-serif drop-shadow-lg">Heradexia:</span>
          <br />
          <span className="italic text-white font-light">
            <span className="text-white font-semibold">Your </span>
            <span className="text-purple-400 font-extrabold tracking-wider">
              <Typewriter options={{ strings: ['Visual Textbook'], autoStart: true, loop: true }} />
            </span>
            <span className="text-white font-semibold"> Has Arrived</span>
          </span>
        </motion.h2>

        <p className="text-lg text-gray-200 mb-10 max-w-2xl mx-auto">
          Learn smarter. Retain more. Join a movement that’s transforming education, one scroll at a time.
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-6">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email to join waitlist"
            required
            className="px-4 py-3 rounded-md text-black w-full sm:w-96 text-base shadow-inner"
          />
          <button
            type="submit"
            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 font-semibold text-white shadow-lg hover:scale-105 transition-all"
          >
            <Sparkles className="w-5 h-5 text-white" />
            Join the Movement
          </button>
        </form>

        {submitted && (
          <p className="text-green-400 font-medium">
            🎉 You’re on the list! We’ll keep you posted.
          </p>
        )}

        {waitlistCount !== null && (
          <p className="text-sm text-gray-400 mt-2">
            🔥 {waitlistCount}+ students already waitlisted!
          </p>
        )}

        <motion.section
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2 }}
          className="mt-16 bg-[#112240] bg-opacity-90 p-10 rounded-2xl shadow-xl border border-[#1F2937]"
        >
          <h3 className="text-2xl font-bold mb-6 text-purple-300 border-b border-purple-300 pb-2 w-fit mx-auto">
            Early Access Perks
          </h3>
          <ul className="grid sm:grid-cols-3 gap-6 text-gray-300 text-left text-base">
            <li className="flex items-center space-x-2">
              <span>⚡</span>
              <span>Priority beta access</span>
            </li>
            <li className="flex items-center space-x-2">
              <span>🎁</span>
              <span>Free premium for 3 months </span>
            </li>
            <li className="flex items-center space-x-2">
              <span>🎓</span>
              <span>Founding Learner badge</span>
            </li>
          </ul>

          <p className="mt-8 text-gray-400 text-sm leading-relaxed text-center">
            Built on the <strong className="text-white">Heradexia Method</strong>. <br />
            Backed by mentors. Powered by A.I. <br />
            A part of the Ecosystem.
          </p>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.3 }}
          className="mt-20 max-w-3xl mx-auto text-left bg-[#122B3E] p-8 rounded-xl border border-[#8BC53F] shadow-lg"
        >
          <h3 className="text-3xl font-bold text-[#8BC53F] mb-4 text-center">What is Heradexia?</h3>
          <p className="text-gray-200 leading-relaxed">
            Heradexia is your visual textbook — a scrollable, interactive education experience where every short-form video is designed to make knowledge stick. It’s powered by the 6-step Heradexia Method, grounded in cognitive science, and guided by real mentors. Whether you're preparing for exams, tackling tough topics, or just curious to learn deeply, Heradexia makes learning visual, spiritual, and unforgettable.
          </p>
        </motion.section>
      </main>

      <footer className="mt-32 text-center text-sm text-gray-400 py-6 border-t border-gray-700">
        © 2025 Heradexia • Built with love and purpose.
      </footer>
    </div>
  );
}



